import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, Search } from 'lucide-react';

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectFilterProps {
  /** Button label, e.g. "Organizations" — shows "(n)" when selections exist */
  label: string;
  /**
   * Server-backed option lookup, called with the debounced search term
   * (empty string on open = first page). Must be a stable function
   * (module scope), like SearchSelect fetchers.
   */
  fetchOptions: (term: string) => Promise<MultiSelectOption[]>;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Toolbar multi-select filter: a compact button opening a searchable checkbox
 * list. Options are fetched from the server per search term, so catalogs
 * larger than one page (e.g. hundreds of organizations) are fully reachable.
 * Selected items are pinned at the top so they stay visible and unselectable
 * regardless of the current search.
 */
const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  fetchOptions,
  selectedIds,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Remembers every id→label ever seen, so pinned selections keep their
  // names even when the current search results don't include them.
  const labelCache = useRef<Map<string, string>>(new Map());
  const selected = new Set(selectedIds);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await fetchOptions(term.trim());
        if (!cancelled) {
          results.forEach((option) => labelCache.current.set(option.id, option.label));
          setOptions(results);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, term ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // fetchOptions is expected to be stable (module scope)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, term]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const optionRow = (id: string, optionLabel: string) => (
    <label
      key={id}
      className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-text-primary hover:bg-background-tertiary dark:text-text-dark-primary dark:hover:bg-background-dark-tertiary"
    >
      <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} />
      <span className="min-w-0 truncate">{optionLabel}</span>
    </label>
  );

  const unselectedOptions = options.filter((option) => !selected.has(option.id));

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-9 items-center gap-1 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
      >
        {label}
        {selected.size > 0 && (
          <span className="rounded-full bg-primary-100 px-1.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:bg-opacity-40 dark:text-primary-300">
            {selected.size}
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-text-tertiary dark:text-text-dark-tertiary" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 flex max-h-80 w-72 flex-col rounded-lg border border-border-light bg-white shadow-lg dark:border-border-dark dark:bg-background-dark-secondary">
          <div className="relative border-b border-border-light p-2 dark:border-border-dark">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary" />
            <input
              type="text"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={`Search ${label.toLowerCase()}…`}
              aria-label={`Search ${label.toLowerCase()}`}
              autoFocus
              className="h-8 w-full rounded-md border border-border-light bg-white pl-8 pr-8 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-primary dark:text-text-dark-primary"
            />
            {loading && (
              <Loader2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-text-tertiary dark:text-text-dark-tertiary" />
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
            {selected.size > 0 && (
              <>
                <div className="flex items-center justify-between px-3 pb-1 pt-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                    Selected
                  </span>
                  <button
                    onClick={() => onChange([])}
                    className="text-xs font-medium text-primary-700 hover:underline dark:text-primary-400"
                  >
                    Clear all
                  </button>
                </div>
                {selectedIds.map((id) => optionRow(id, labelCache.current.get(id) ?? id))}
                <div className="my-1 border-t border-border-light dark:border-border-dark" />
              </>
            )}
            {unselectedOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
                {loading ? 'Loading…' : term ? 'No matches' : 'No options'}
              </p>
            ) : (
              unselectedOptions.map((option) => optionRow(option.id, option.label))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
